import { format } from 'date-fns';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    BrochureBasketDetail,
    BrochureDetail,
    BrochureOrderProduct,
    OrderProductVariant,
    getBrochureBasketById,
    getBrochureById,
    getBrochureProductImage,
    getOrderProducts,
} from '../../api/brochures';
import {
    createBcOrder,
    deleteDraftOrder,
    getAsyncBcOrderTotal,
    getCalculatedLinePrice,
    getCalculatedPrice,
    getInventoryAvailability,
    getOrderTotals,
} from '../../api/business-central';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import LoadingModal from '../../components/LoadingModal/LoadingModal';
import MessageToastComponent from '../../components/MessageToastComponent/MessageToastComponent';
import { getRequestedLanguages } from '../../locale/languages';
import {
    brochureCartHash,
    brochureMinOrderQuantity,
    enableBrochureStockCheck,
    enableZakekeAttributes,
    orderedSizesArray,
} from '../../utils/constants';
import { ORDER_TYPE_CODE } from '../../utils/enum/OrderTypeCode';
import {
    convertNumberToCurrency,
    getOptionIndex,
    getOptionValue,
    parseQuantity,
    productIgnoresStockCheck,
    removeSizeFromSku,
} from '../../utils/functions';
import { useCustomerCurrency } from '../../utils/hooks/useCustomerCurrency';
import { SelectedCustomerData, TCalculatedTotals } from '../../utils/models/cart';
import { BcOrder, InventoryAvailability } from '../../utils/models/order';
import { OrderSizeCol } from './OrderSizeCol';

type Quantities = Record<string, Record<string, number>>;

type SelectedVariant = OrderProductVariant & {
    lineNo: number;
    size: string;
    quantity: number;
    product: BrochureOrderProduct;
};

type MergedOrderTotals = {
    itemsCount: number;
    subTotal: string;
    totalDiscountAmount: string;
    totalExclVat: string;
};

const buildUniqueId = (product: BrochureOrderProduct) =>
    product.custom_product ? `${product.id}-${product.custom_product.id}` : product.id.toString();

const computeTotal = (quantities: Record<string, number>): number =>
    Object.values(quantities).reduce((sum, v) => sum + (v || 0), 0);

const getVariantOptions = (
    option1: OrderProductVariant['option1'],
    option2: OrderProductVariant['option2'],
    option3: OrderProductVariant['option3'],
    product: BrochureOrderProduct,
) => {
    const options: { name: string | undefined; value: string }[] = [];

    if (option1) {
        options.push({ name: product.options[0]?.name, value: option1 });
    }

    if (option2) {
        options.push({ name: product.options[1]?.name, value: option2 });
    }

    if (option3) {
        options.push({ name: product.options[2]?.name, value: option3 });
    }

    return options;
};

const mergeOrderTotals = (
    orderTotalsResponse: TCalculatedTotals[],
    selectedVariantQuantities: SelectedVariant[],
    currencyCode: string,
) => {
    const totalExclVat = orderTotalsResponse.reduce(
        (orderTotals, calculatedTotals) => orderTotals + (calculatedTotals.TotalExclVat ?? 0),
        0,
    );

    const selectedVariantTotals = selectedVariantQuantities.reduce(
        ({ itemsCount, subTotal, discount }, variant) => {
            const quantity = variant.quantity ?? 0;
            const variantSubTotal = variant?.fetchedPrices?.price ?? 0;
            const variantDiscount = variant?.fetchedPrices?.discountAmount ?? 0;

            return {
                itemsCount: itemsCount + quantity,
                subTotal: subTotal + variantSubTotal * quantity,
                discount: discount + variantDiscount * quantity,
            };
        },
        {
            itemsCount: 0,
            subTotal: 0,
            discount: 0,
        },
    );

    const mergedOrderTotals: MergedOrderTotals = {
        itemsCount: selectedVariantTotals.itemsCount,
        subTotal: convertNumberToCurrency(selectedVariantTotals.subTotal, currencyCode),
        totalDiscountAmount: convertNumberToCurrency(selectedVariantTotals.discount, currencyCode),
        totalExclVat: convertNumberToCurrency(totalExclVat, currencyCode),
    };

    return mergedOrderTotals;
};

interface Props {
    basketId: string;
    brochureId: string;
    onBack: () => void;
}

const BrochureOrderView: FC<Props> = ({ basketId, brochureId, onBack }) => {
    const language = getRequestedLanguages(window.errea_accountUserData.language);
    const t = language.brochures.order;
    const tDetail = language.brochures.detail;
    const lang = (window.errea_accountUserData.language || 'it').split('-')[0].toLowerCase();
    const orderErrors = language.brochures.order.error;

    const [basketData, setBasketData] = useState<BrochureBasketDetail | null>(null);
    const [brochureData, setBrochureData] = useState<BrochureDetail | null>(null);
    const [orderProducts, setOrderProducts] = useState<BrochureOrderProduct[]>([]);
    const [quantities, setQuantities] = useState<Quantities>({});
    const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [priceCalculated, setPriceCalculated] = useState(false);
    const [error, setError] = useState('');
    const draftOrderNoRef = useRef<string | null>(localStorage.getItem('errea_brochureDraftOrderNumber'));
    const [orderTotals, setOrderTotals] = useState<MergedOrderTotals | null>(null);
    const [errorProductIds, setErrorProductIds] = useState<Set<string>>(new Set());
    const [selectedVariantQuantities, setSelectedVariantQuantities] = useState<SelectedVariant[]>([]);
    const productsListRef = useRef<HTMLDivElement>(null);

    const selectedCustomer = useMemo(() => {
        const customerData = localStorage.getItem('errea_selectedCustomer');
        return customerData ? (JSON.parse(customerData) as SelectedCustomerData) : null;
    }, []);
    const { code: currencyCode } = useCustomerCurrency();

    useEffect(() => {
        if (!basketId || !brochureId || !selectedCustomer) return;
        setIsLoading(true);

        Promise.all([
            getBrochureBasketById(basketId, lang),
            getBrochureById(basketId, brochureId),
            getOrderProducts(basketId, brochureId, lang),
        ])
            .then(async ([basketResult, brochureResult, orderProducts]) => {
                setBasketData(basketResult);
                setBrochureData(brochureResult);

                const productAvailability = await Promise.all(
                    orderProducts.flatMap((product) => {
                        const sizeOptionIndex = getOptionIndex(
                            product.options.map((option) => option.name),
                            'taglia',
                        );

                        const variantAvailabilityPromises = product.variants.map(async (variant) => {
                            const variantSize = getOptionValue(sizeOptionIndex, variant, 'UNICA');

                            const availability = (await getInventoryAvailability(
                                removeSizeFromSku(variant.sku),
                                variantSize,
                            )) as InventoryAvailability;

                            return {
                                sku: variant.sku,
                                availability,
                            };
                        });

                        return variantAvailabilityPromises;
                    }),
                );

                const productPrices = await Promise.all(
                    orderProducts.flatMap((product) => {
                        const sizeOptionIndex = getOptionIndex(
                            product.options.map((option) => option.name),
                            'taglia',
                        );

                        const variantPricesPromises = product.variants.map(async (variant) => {
                            const variantSize = getOptionValue(sizeOptionIndex, variant, 'UNICA');

                            const prices = await getCalculatedPrice(
                                selectedCustomer.customerNo,
                                removeSizeFromSku(variant.sku),
                                variantSize,
                                ORDER_TYPE_CODE.Vendi,
                            );

                            return {
                                sku: variant.sku,
                                prices,
                            };
                        });

                        return variantPricesPromises;
                    }),
                );

                const productWithCompleteVariants = orderProducts.map((product) => {
                    const productOptions = product.options.map((option) => option.name);

                    const variantsWithAvailability = product.variants.map((variant) => {
                        const inventoryAvailability = productAvailability.find((item) => item.sku === variant.sku);
                        const prices = productPrices.find((item) => item.sku === variant.sku);
                        const sizeIndex = getOptionIndex(productOptions, 'taglia');
                        const size = getOptionValue(sizeIndex, variant, 'UNICA');

                        const ignoreStockCheck = !enableBrochureStockCheck || productIgnoresStockCheck(variant);

                        return {
                            ...variant,
                            deliveryPeriod: product.delivery_period ?? '',
                            pfsDeliveryItem: !!product.pfs_item_status,
                            size,
                            inventoryQuantity:
                                inventoryAvailability && !ignoreStockCheck
                                    ? inventoryAvailability.availability.value
                                    : 0,
                            fetchedPrices: prices?.prices?.value,
                        };
                    });

                    variantsWithAvailability.sort((a, b) => {
                        const indexA = orderedSizesArray.indexOf(a.size);
                        const indexB = orderedSizesArray.indexOf(b.size);
                        return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
                    });

                    return {
                        ...product,
                        variants: variantsWithAvailability,
                    };
                });

                setOrderProducts(productWithCompleteVariants);
            })
            .catch(() => {
                setBasketData(null);
                setBrochureData(null);
                setOrderProducts([]);
            })
            .finally(() => setIsLoading(false));
    }, [basketId, brochureId, lang, selectedCustomer]);

    useEffect(() => {
        const brochureCartString = localStorage.getItem('errea_brochureCheckout');
        const baseQuantities: Quantities = {};

        if (brochureCartString) {
            const brochureCart = JSON.parse(brochureCartString);

            brochureCart.items.forEach((item: any) => {
                const id = item?.id;
                const sku = item?.sku;

                if (id && sku) {
                    baseQuantities[id] = baseQuantities[id] || {};
                    baseQuantities[id][sku] = Number(item.quantity);
                }
            });
        }

        setQuantities((prevQuantities) => {
            const nextQuantities: Quantities = { ...baseQuantities };

            orderProducts.forEach((product) => {
                const uniqueId = buildUniqueId(product);
                const productQuantities = { ...(prevQuantities[uniqueId] ?? {}) };

                product.variants.forEach((variant) => {
                    productQuantities[variant.sku] = productQuantities[variant.sku] ?? 0;
                });

                nextQuantities[uniqueId] = productQuantities;
            });

            return nextQuantities;
        });
    }, [orderProducts]);

    useEffect(() => {
        const selectedVariants: Omit<SelectedVariant, 'lineNo'>[] = [];

        Object.entries(quantities).forEach(([uniqueId, variantQuantities]) => {
            Object.entries(variantQuantities).forEach(([sku, rawQuantity]) => {
                const product = orderProducts.find((item) => buildUniqueId(item) === uniqueId);
                const quantity = parseQuantity(rawQuantity);

                if (!quantity || !product) {
                    return;
                }

                const variant = product.variants.find((productVariant) => productVariant.sku === sku);
                if (!variant) {
                    return;
                }

                const sizeOptionIndex = getOptionIndex(
                    product.options.map((option) => option.name),
                    'taglia',
                );
                const size = getOptionValue(sizeOptionIndex, variant, 'UNICA');

                selectedVariants.push({
                    ...variant,
                    size,
                    quantity,
                    product,
                });
            });
        });

        setSelectedVariantQuantities(
            selectedVariants.map((incompleteVariant, index) => ({ ...incompleteVariant, lineNo: index + 1 })),
        );
    }, [orderProducts, quantities]);

    const handleQuantityChange = useCallback(
        (product: BrochureOrderProduct, variant: OrderProductVariant, value: string) => {
            setPriceCalculated(false);
            const uniqueId = buildUniqueId(product);
            const newValue = parseQuantity(value);
            const ignoreStockCheck = !enableBrochureStockCheck || productIgnoresStockCheck(variant);

            setErrorProductIds((prev) => {
                if (!prev.has(uniqueId)) return prev;
                const next = new Set(prev);
                next.delete(uniqueId);
                return next;
            });
            setQuantities((prev) => ({
                ...prev,
                [uniqueId]: {
                    ...prev[uniqueId],
                    [variant.sku]:
                        variant.inventoryQuantity === undefined || ignoreStockCheck
                            ? newValue
                            : Math.min(newValue, variant.inventoryQuantity),
                },
            }));
        },
        [orderProducts],
    );

    const checkOrderValidity = useCallback(() => {
        const invalidUniqueIds = new Set<string>();

        const oddVariant = selectedVariantQuantities.find(
            (variant) => variant.quantity % 2 !== 0 && variant.product.custom_product,
        );
        if (oddVariant) {
            const oddUniqueId = buildUniqueId(oddVariant.product);
            invalidUniqueIds.add(oddUniqueId);
        }

        const groupedProducts = selectedVariantQuantities.reduce(
            (groups, variant) => {
                if (!variant.product.custom_product) {
                    return groups;
                }

                const uniqueId = buildUniqueId(variant.product);
                if (!groups[uniqueId]) {
                    groups[uniqueId] = { variants: [], totalQuantity: 0 };
                }
                groups[uniqueId].variants.push(variant);
                groups[uniqueId].totalQuantity += variant.quantity ?? 0;
                return groups;
            },
            {} as Record<string, { variants: SelectedVariant[]; totalQuantity: number }>,
        );

        Object.entries(groupedProducts).forEach(([uniqueId, product]) => {
            if (product.totalQuantity < brochureMinOrderQuantity) {
                invalidUniqueIds.add(uniqueId);
            }
        });

        setErrorProductIds(invalidUniqueIds);

        if (invalidUniqueIds.size > 0) {
            if (oddVariant) {
                setError(orderErrors.quantity_not_even);
            } else {
                setError(orderErrors.min_quantity.replace('{{min}}', String(brochureMinOrderQuantity)));
            }

            const firstId = Array.from(invalidUniqueIds)[0];
            if (productsListRef.current) {
                const card = productsListRef.current.querySelector(`[data-unique-id="${firstId}"]`);
                if (card) {
                    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }

            return false;
        }

        return true;
    }, [selectedVariantQuantities, orderErrors]);

    const createDraftOrder = useCallback(async () => {
        if (!selectedCustomer) {
            setError(orderErrors.missing_customer);
            return null;
        }

        if (!selectedVariantQuantities.length) {
            setError(orderErrors.quantity_required);
            return null;
        }

        const timestamp = Math.floor(Date.now() / 1000);
        const documentNo = `B${selectedCustomer.salesPersonCode || selectedCustomer.customerNo}-${timestamp}`;

        const orderData: BcOrder = {
            documentType: 'Order',
            documentNo,
            brandCode: selectedVariantQuantities[0]?.product?.vendor || 'ERREÀ',
            salesPersonCode: selectedCustomer.salesPersonCode || '',
            customerNo: selectedCustomer.customerNo || '',
            customerPriceGroup: selectedCustomer.customerPriceGroup || '',
            shipToCode: '',
            orderTypeCode: ORDER_TYPE_CODE.Vendi,
            ...(enableZakekeAttributes
                ? {
                      szkBrochureDescription: brochureData?.name || '',
                      szkCompanyName: basketData?.company || '',
                  }
                : {}),
            orderLines: selectedVariantQuantities.map((variant) => ({
                lineNo: variant.lineNo,
                documentNo,
                customerNo: selectedCustomer.customerNo || '',
                ...(enableZakekeAttributes && !!variant.product.custom_product
                    ? {
                          szkConfigurationID: variant.product.custom_product.zakeke_design_id,
                          szkCustomIDTemplate: removeSizeFromSku(variant.sku),
                          description2: variant.product.custom_product.zakeke_front_area_colors ?? '',
                      }
                    : {}),
                lineType: 'Item',
                itemNo: removeSizeFromSku(variant.sku),
                itemQuantity: variant.quantity,
                promotionCode: '',
                campaignCode: '',
                variantCode: variant.size,
            })),
        };

        const draftResult = await createBcOrder(orderData)
            .then((result) => result as BcOrder)
            .catch((error) => {
                setError(typeof error === 'string' ? error : orderErrors.draft_creation);
                return null;
            });

        return draftResult;
    }, [orderErrors, selectedCustomer, selectedVariantQuantities]);

    const handleCalculatePrice = useCallback(async () => {
        const isOrderValid = checkOrderValidity();
        if (!isOrderValid) {
            return;
        }

        setIsCalculatingPrice(true);
        setError('');

        try {
            if (draftOrderNoRef.current) {
                await deleteDraftOrder(draftOrderNoRef.current).catch(() => null);
                draftOrderNoRef.current = null;
            }

            const draftOrder = await createDraftOrder();
            if (!draftOrder) {
                return;
            }

            draftOrderNoRef.current = draftOrder.documentNo;

            const result: any = await getAsyncBcOrderTotal(draftOrder.documentNo, '1');
            const orderTotalsResponse: TCalculatedTotals[] = await getOrderTotals(result.pid)
                .then((response) => {
                    if (!response?.value?.OrderTotals) {
                        setError(orderErrors.price_calculation);
                        return [];
                    }

                    localStorage.removeItem('errea_brochureCheckout');
                    localStorage.removeItem('errea_brochurePage');
                    localStorage.setItem('errea_brochureDraftOrderNumber', draftOrder.documentNo);
                    localStorage.setItem('errea_brochureCalculatedTotals', JSON.stringify(response));
                    return response.value.OrderTotals;
                })
                .catch(() => {
                    setError(orderErrors.price_calculation);
                    return [];
                });

            if (orderTotalsResponse.length === 0) {
                setError(orderErrors.price_calculation);
                setOrderTotals(null);
                return;
            }

            const linePricePromises = selectedVariantQuantities.map(async (variant) => ({
                lineNo: variant.lineNo,
                fetchedPrices: await getCalculatedLinePrice(draftOrder.documentNo, '1', variant.lineNo),
            }));
            const updatedLinePrices = await Promise.all(linePricePromises);

            const variantUpdatedPrices = selectedVariantQuantities.map((variant) => {
                const updatedLine = updatedLinePrices.find((line) => line.lineNo === variant.lineNo);
                if (updatedLine) {
                    return {
                        ...variant,
                        fetchedPrices: updatedLine.fetchedPrices,
                    };
                }

                return variant;
            });
            setSelectedVariantQuantities(variantUpdatedPrices);

            const orderTotals = mergeOrderTotals(orderTotalsResponse, variantUpdatedPrices, currencyCode);
            setOrderTotals(orderTotals);
            setPriceCalculated(true);
        } catch (error) {
            setOrderTotals(null);
            setError(typeof error === 'string' ? error : orderErrors.price_calculation);
        } finally {
            setIsCalculatingPrice(false);
        }
    }, [createDraftOrder, orderErrors, selectedVariantQuantities, setSelectedVariantQuantities]);

    const handleConfirmOrder = useCallback(async () => {
        const isOrderValid = checkOrderValidity();
        if (!isOrderValid) {
            return;
        }

        if (!selectedVariantQuantities.length) {
            setError(orderErrors.quantity_required);
            return;
        }

        if (!selectedCustomer) {
            setError(orderErrors.missing_customer);
            return null;
        }

        try {
            await handleCalculatePrice();

            const cartData = {
                cartName: language.brochures.order.cart_name,
                brandName: selectedVariantQuantities[0]?.product?.vendor || 'ERREÀ',
                items: selectedVariantQuantities.map((variant, index) => ({
                    id: buildUniqueId(variant.product),
                    line: `${index + 1}`,
                    productType:
                        variant.product.category ??
                        (variant.product.custom_product ? t.custom_product_type : t.default_product_type),
                    cart: 'catalog',
                    productId: variant.product.id,
                    variantId: variant.ecommerce_id?.split('/').pop(),
                    code: removeSizeFromSku(variant.sku),
                    quantity: variant.quantity,
                    max: null,
                    url: '#',
                    title: variant.product.name,
                    sku: variant.sku,
                    featuredImage: getBrochureProductImage(variant.product, variant.product.custom_product),
                    collectionName: brochureData?.name ?? language.brochures.order.cart_name,
                    brandName: variant.product.vendor || 'ERREÀ',
                    fetchedPrices: variant.fetchedPrices,
                    options: getVariantOptions(variant.option1, variant.option2, variant.option3, variant.product),
                    deliveryPeriod: variant.deliveryPeriod,
                    pfsDeliveryItem: variant.pfsDeliveryItem,
                    ...(variant.product.custom_product
                        ? {
                              zakekeDesignId: variant.product.custom_product.zakeke_design_id ?? '',
                          }
                        : {}),
                })),
            };

            const checkoutPayload = { ...cartData, cartType: 'catalog' };
            localStorage.setItem('errea_brochureCheckout', JSON.stringify(checkoutPayload));

            localStorage.setItem(
                'errea_brochurePage',
                JSON.stringify({
                    pathname: window.location.pathname,
                    basketId: basketId,
                    brochureId: brochureId,
                    basketName: basketData?.name ?? '',
                    basketCompany: basketData?.company ?? '',
                }),
            );

            window.location.href = `${window?.Shopify?.routes?.root ?? '/'}cart#${brochureCartHash}`;
        } catch (error) {
            setError(typeof error === 'string' ? error : orderErrors.confirmation);
        }
    }, [basketId, brochureData, selectedVariantQuantities, selectedCustomer]);

    return (
        <div className="brochureOrder">
            <LoadingModal isVisible={isCalculatingPrice || isLoading} />
            <MessageToastComponent message={error} type={'error'} onHide={() => setError('')} />
            <a
                href="#"
                className="brochureDetailBack"
                onClick={(e) => {
                    e.preventDefault();
                    onBack();
                }}
            >
                <i className="icon errea-arrow-left"></i>
                <span>{tDetail.go_back}</span>
            </a>

            <h1 className="brochureOrderTitle">{t.title}</h1>

            <div className="brochureOrderMeta">
                <div className="brochureOrderMetaItem">
                    <span className="brochureOrderMetaLabel">{language.brochures.list.collection_label}</span>
                    <span className="brochureOrderMetaValue">{basketData?.name ?? '--'}</span>
                </div>
                <div className="brochureOrderMetaItem">
                    <span className="brochureOrderMetaLabel">{tDetail.last_modified}</span>
                    <span className="brochureOrderMetaValue">
                        {basketData?.updated_at
                            ? format(new Date(basketData.updated_at), language.general.date_format)
                            : '--'}
                    </span>
                </div>
                <div className="brochureOrderMetaItem">
                    <span className="brochureOrderMetaLabel">{language.brochures.list.company_label}</span>
                    <span className="brochureOrderMetaValue">{basketData?.company ?? '--'}</span>
                </div>
            </div>

            <div className="brochureOrderContent">
                <div className="brochureOrderProductsList" ref={productsListRef}>
                    {orderProducts.length > 0 ? (
                        orderProducts.map((product, index) => {
                            const uniqueId = buildUniqueId(product);
                            const total = computeTotal(quantities[uniqueId] ?? {});
                            const productImage = getBrochureProductImage(product, product.custom_product);
                            const hasError = errorProductIds.has(uniqueId);

                            return (
                                <div
                                    key={`brochureOrderProductCard-${index}`}
                                    className="brochureOrderProductCard"
                                    data-unique-id={uniqueId}
                                >
                                    <div className="brochureOrderProductTop">
                                        <div className="brochureOrderProductImage">
                                            {!!productImage ? (
                                                <img src={productImage} alt={product.name} />
                                            ) : (
                                                <div className="brochureOrderProductImagePlaceholder" />
                                            )}
                                        </div>
                                        <div className="brochureOrderProductInfo">
                                            {product.category && (
                                                <span className="brochureOrderProductCategory">{product.category}</span>
                                            )}
                                            <span className="brochureOrderProductCode">{product.sku}</span>
                                            <span className="brochureOrderProductName">{product.name}</span>
                                            <span className="brochureOrderProductColor">{product.color}</span>
                                        </div>
                                    </div>

                                    <div className="brochureOrderSizesWrapper">
                                        <div className="brochureOrderSizesGrid">
                                            {product.variants.map((variant) => (
                                                <OrderSizeCol
                                                    key={variant.ecommerce_id}
                                                    product={product}
                                                    variant={variant}
                                                    quantityValue={quantities[uniqueId]?.[variant.sku] ?? 0}
                                                    onQuantityChange={(value) =>
                                                        handleQuantityChange(product, variant, value)
                                                    }
                                                />
                                            ))}
                                            <div
                                                className={`brochureOrderSizeCol brochureOrderTotalCol${hasError ? ' brochureOrderTotalCol--error' : ''}`}
                                            >
                                                <div className="brochureOrderSizeHeader brochureOrderSizeHeaderTotal">
                                                    <span className="brochureOrderSizeLabel">{t.total}</span>
                                                </div>
                                                <div
                                                    className={`brochureOrderTotalValue${hasError ? ' brochureOrderTotalValue--error' : ''}`}
                                                >
                                                    {total || '-'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <span>{!isLoading ? language.brochures.order.error.missing_custom_products : '--'}</span>
                    )}
                </div>

                <div className="brochureOrderSidebar">
                    <h2 className="brochureOrderSidebarTitle">{t.order_summary}</h2>
                    <p className="brochureOrderSidebarNote">{t.total_next_step}</p>
                    <div className="brochureOrderSidebarRows">
                        <div className="brochureOrderSidebarRow">
                            <span>{t.num_items}</span>
                            <span>{orderTotals ? orderTotals.itemsCount : '-'}</span>
                        </div>
                        <div className="brochureOrderSidebarRow">
                            <span>{t.subtotal}</span>
                            <span>{orderTotals ? orderTotals.subTotal : '-'}</span>
                        </div>
                        <div className="brochureOrderSidebarRow">
                            <span>{t.discount}</span>
                            <span>{orderTotals ? orderTotals.totalDiscountAmount : '-'}</span>
                        </div>
                    </div>
                    <div className="brochureOrderSidebarTotal">
                        <span>{t.total_excl_vat}</span>
                        <span>{orderTotals ? orderTotals.totalExclVat : '-'}</span>
                    </div>
                    <ButtonComponent
                        style="outline"
                        text={isCalculatingPrice ? t.calculating_price : t.calculate_price}
                        icon_left="refresh"
                        onClick={handleCalculatePrice}
                        disabled={isCalculatingPrice || selectedVariantQuantities.length === 0}
                    />
                    <ButtonComponent
                        style="solid"
                        text={t.send_order}
                        icon_right="arrow-right"
                        onClick={handleConfirmOrder}
                        disabled={isCalculatingPrice || selectedVariantQuantities.length === 0 || !priceCalculated}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrochureOrderView;
